import { ReactElement, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y } from 'swiper/modules';
import 'swiper/css';

import useSwiperRef from './useSwiperRef';
import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import TitleFlip from '../../helpers/TitleFlip';
import { LazyLoad } from '@Hotels-at-Home/hah-fe-core/dist/components';

const PillowsSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$pillowsSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$pillows' });

  const videoRef = useRef<HTMLVideoElement>();
  const playbackControl = (play: 'play' | 'pause') => {
    if (!videoRef.current) {
      return null;
    }
    return play === 'play' ? videoRef.current.play() : videoRef.current.pause();
  };

  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();

  return (
    <section className="base-section pillows-section pillows" aria-labelledby="pillowsSectionTitle">
      <h2 id="pillowsSectionTitle" className="mob-title">
        <TitleFlip normalText={t('marriott')}>
          <span className="accent-bar">
            {t('pillows')}
            <em data-gsap="grow" data-gsap-delay="0.06" data-gsap-duration="0.8" />
          </span>
        </TitleFlip>
      </h2>
      <div className="swiper-buttons">
        <button
          data-gsap="fade-right"
          data-gsap-delay="0.65"
          type="button"
          className="swiper-button swiper-button-prev"
          aria-label={t('prevSlide')}
          ref={prevElRef}
        >
          <span className="icon-arrow" />
        </button>
        <button
          data-gsap="fade-left"
          data-gsap-delay="0.6"
          type="button"
          className="swiper-button swiper-button-next"
          aria-label={t('nextSlide')}
          ref={nextElRef}
        >
          <span className="icon-arrow" />
        </button>
      </div>
      <div className="pillow-products">
        <div data-gsap="fade" data-gsap-delay="0.03" className="pillow-product-wrap title-wrap">
          <div className="stack">
            <h2 data-gsap="fade-up" data-gsap-delay="0.6" className="section-title">
              <TitleFlip normalText={t('marriott')}>
                <span className="accent-bar">
                  {t('pillows')}
                  <em data-gsap="grow" data-gsap-delay="0.8" data-gsap-duration="0.8" />
                </span>
              </TitleFlip>
            </h2>
            <p data-gsap="fade-up" data-gsap-delay="0.7">
              <Button
                href={tLink('pillows')}
                variant="plain"
                ariaLabel={`${tButton('shopNow')}: ${t('marriottPillow')}`}
                id="btn_HP_pillows_shopnow"
              >
                {tButton('shopNow')}
              </Button>
            </p>
          </div>
        </div>

        <div
          className="pillow-product-wrap slider-wrap"
          aria-roledescription="carousel"
          role="group"
        >
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slidesPerView={1}
            loop
            navigation={{
              prevEl,
              nextEl,
            }}
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-pillows"
            modules={[Navigation, A11y]}
          >
            <SwiperSlide>
              <div className="top-wrap">
                <a href={tLink('marriottPillow')} className="hover-btn-animation">
                  <picture>
                    <source srcSet="/images/homePage/pillowsSection/hp-the-marriott-pillow.webp" />
                    <img
                      data-gsap="fade"
                      data-gsap-delay="0.4"
                      src="/images/homePage/pillowsSection/hp-the-marriott-pillow.webp"
                      alt={tCommon('productName', { productName: t('marriottPillow') })}
                      className="img"
                      loading="lazy"
                      width={806}
                      height={538}
                      decoding="async"
                      fetchPriority="low"
                    />
                  </picture>
                  <h3 className="section-subtitle">
                    <span data-gsap="fade-left" className="txt" data-gsap-bottom="0">
                      {t('marriottPillow')}
                    </span>
                  </h3>
                  <div className="hover-content">
                    <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('marriottPillow')}`}>
                      {tButton('shopNow')}
                    </Button>
                  </div>
                </a>
              </div>
              <div data-gsap="fade-up" data-gsap-duration="0.6" className="bot-wrap">
                <div className="bot-content">
                  <span className="mar-icon-pillow-split" />
                  <span className="txt">{t('dualChamber')}</span>
                </div>
                <div className="bot-content">
                  <span className="mar-icon-eco-friendly" />
                  <span className="txt">{t('ecoFriendlyDesign')}</span>
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="top-wrap">
                <a
                  href={tLink('featherDownPillow')}
                  id="HP_pillows_feather-down-pillow"
                  className="hover-btn-animation"
                >
                  <picture>
                    <source srcSet="/images/homePage/pillowsSection/hp-marriott-feather-down-pillows.webp" />
                    <img
                      data-gsap="fade"
                      data-gsap-delay="0.4"
                      src="/images/homePage/pillowsSection/hp-marriott-feather-down-pillows.webp"
                      alt={tCommon('productName', { productName: t('featherAndDownPillow') })}
                      className="img"
                      loading="lazy"
                      width={806}
                      height={538}
                      decoding="async"
                      fetchPriority="low"
                    />
                  </picture>
                  <h3 className="section-subtitle">
                    <span data-gsap="fade-left" className="txt" data-gsap-bottom="0">
                      {t('featherAndDownPillow')}
                    </span>
                  </h3>
                  <div className="hover-content">
                    <Button
                      noTabbing
                      ariaLabel={`${tButton('shopNow')}: ${t('featherAndDownPillow')}}`}
                    >
                      {tButton('shopNow')}
                    </Button>
                  </div>
                </a>
              </div>
              <div className="bot-wrap">
                <div className="bot-content">
                  <span className="mar-icon-feather" />
                  <span className="txt">{t('optimalLoft')}</span>
                </div>
                <div className="bot-content">
                  <span className="mar-icon-allergen-free" />
                  <span className="txt">{t('allergenFree')}</span>
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="top-wrap">
                <a href={tLink('fibrePillow')} className="hover-btn-animation">
                  <picture>
                    <source srcSet="/images/homePage/pillowsSection/hp-marriott-eco-pillow.webp" />
                    <img
                      data-gsap="fade"
                      data-gsap-delay="0.4"
                      src="/images/homePage/pillowsSection/hp-marriott-eco-pillow.webp"
                      alt={tCommon('productName', { productName: t('fibrePillow') })}
                      className="img"
                      loading="lazy"
                      width={806}
                      height={538}
                      decoding="async"
                      fetchPriority="low"
                    />
                  </picture>
                  <h3 className="section-subtitle">
                    <span data-gsap="fade-left" className="txt" data-gsap-bottom="0">
                      {t('fibrePillow')}
                    </span>
                  </h3>
                  <div className="hover-content">
                    <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('fibrePillow')}`}>
                      {tButton('shopNow')}
                    </Button>
                  </div>
                </a>
              </div>
              <div className="bot-wrap">
                <div className="bot-content">
                  <span className="mar-icon-recycled" />
                  <span className="txt">{t('ecoFriendly')}</span>
                </div>
                <div className="bot-content">
                  <span className="mar-icon-feather-free" />
                  <span className="txt">{t('hypoallergenic')}</span>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>

        <div className="pillow-product-wrap image-wrap">
          <a
            className="hover-btn-animation"
            href={tLink('decorativePillows')} // first decorative pillow product
            id="HP_pillows_decorative-pillows"
          >
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
              <source srcSet="/images/homePage/pillowsSection/hp-decorative-pillows.webp" />
              <img
                className="img"
                data-gsap="fade"
                data-gsap-delay="0.4"
                src="/images/homePage/pillowsSection/hp-decorative-pillows.webp"
                alt={tCommon('productName', { productName: t('decorativePillows') })}
                loading="lazy"
                width={806}
                height={538}
                decoding="async"
                fetchPriority="low"
              />
            </picture>
            <h3 className="section-subtitle">
              <span className="txt" data-gsap="fade-left" data-gsap-bottom="0">
                {t('decorativePillows')}
              </span>
            </h3>
            <div className="hover-content">
              <Button noTabbing ariaLabel={`${tButton('shopNow')}: ${t('decorativePillows')}}`}>
                {tButton('shopNow')}
              </Button>
            </div>
          </a>
        </div>
        <div className="pillow-product-wrap video-wrap">
          <img
            src="/video/fill.gif"
            className="img"
            alt={t('pillows')}
            width="1200"
            height="400"
            loading="lazy"
            decoding="async"
            fetchPriority="low"
          />
          <div className="pillow-video-wrap " data-gsap="fade" data-gsap-delay="0.6">
            <LazyLoad offset={500}>
              <div id="hp-video" className="video">
                <video
                  ref={videoRef}
                  loop
                  muted
                  onMouseEnter={() => {
                    playbackControl('play');
                  }}
                  onMouseLeave={() => {
                    playbackControl('pause');
                  }}
                >
                  <source src="/video/MAR-Pillow.mp4" type="video/mp4" />
                  <source src="video/MAR-Pillow.webm" type="video/webm" />
                </video>
              </div>
            </LazyLoad>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PillowsSection;
